import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";

const ToastProviderInstanceContext = createContext(false);
const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

const TOAST_LIMIT = 5;
let toastIdCounter = 0;

export const ToastProvider = ({ children }) => {
  const instanceExists = useContext(ToastProviderInstanceContext);
  const [toasts, setToasts] = useState([]);

  if (instanceExists) {
    throw new Error(
      "Multiple instances of ToastProvider detected. Only one instance is allowed.",
    );
  }

  const addToast = useCallback(
    ({
      message,
      type,
      dataTestId = "toast",
      autoClose = 5000,
      className = "",
      preventCloseOnHover = false,
      preventAutoClose = false,
    }) => {
      if (!message || !type) {
        console.error("Both message and type are required to show a toast.");
        return;
      }
      const id = ++toastIdCounter;
      setToasts((prevToasts) => {
        const updatedToasts = [
          ...prevToasts,
          { id, message, type, dataTestId, autoClose, className, preventCloseOnHover, preventAutoClose },
        ];
        if (updatedToasts.length > TOAST_LIMIT) {
          updatedToasts.shift();
        }
        return updatedToasts;
      });
    },
    [],
  );

  const removeToast = useCallback((id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({ toasts, addToast, removeToast }),
    [toasts, addToast, removeToast],
  );

  return (
    <ToastProviderInstanceContext.Provider value={true}>
      <ToastContext.Provider value={contextValue}>
        {children}
      </ToastContext.Provider>
    </ToastProviderInstanceContext.Provider>
  );
};
